// Central JWT store: kept in memory for the session and mirrored to
// localStorage so a reload restores the session. The API layer reads the
// in-memory copy on every request; on 401 the app clears it and redirects
// to /login. Never log the token value.

import { API_CONFIG } from '../config/api';

const STORAGE_KEY = 'boardwise.auth.token';

let inMemoryToken: string | null = null;

export const authToken = {
  get(): string | null {
    if (inMemoryToken) return inMemoryToken;
    try {
      inMemoryToken = localStorage.getItem(STORAGE_KEY);
    } catch {
      inMemoryToken = null;
    }
    return inMemoryToken;
  },

  set(token: string): void {
    inMemoryToken = token;
    try {
      localStorage.setItem(STORAGE_KEY, token);
    } catch {
      // Storage may be unavailable (private mode); memory copy still works.
    }
  },

  clear(): void {
    inMemoryToken = null;
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

// Callback invoked once when any API call returns 401 so the app can drop the
// session and route to login. Registered by AuthContext.
let unauthorizedHandler: (() => void) | null = null;
export const setUnauthorizedHandler = (handler: (() => void) | null): void => {
  unauthorizedHandler = handler;
};

let interceptorInstalled = false;

// Determine whether a request URL targets our API host (so we only attach the
// bearer to our own backend, never to third parties).
const isApiRequest = (url: string): boolean => {
  const base = API_CONFIG.BASE_URL;
  if (base) return url.startsWith(base);
  // Relative base ('' in container builds): treat same-origin /api/ calls as ours.
  if (url.startsWith('/api/')) return true;
  try {
    return new URL(url, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

// Install a global fetch wrapper that injects the Authorization header on our
// API calls and routes 401s to the unauthorized handler. This covers every
// service in the app (all use raw fetch) without touching each call site.
export const installAuthFetchInterceptor = (): void => {
  if (interceptorInstalled) return;
  interceptorInstalled = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input.url;

    let nextInit = init;
    const token = authToken.get();
    if (token && isApiRequest(url)) {
      const headers = new Headers(init?.headers || (input instanceof Request ? input.headers : undefined));
      if (!headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      nextInit = { ...init, headers };
    }

    const response = await originalFetch(input as RequestInfo, nextInit);

    if (response.status === 401 && isApiRequest(url) && !url.includes('/auth/login')) {
      authToken.clear();
      if (unauthorizedHandler) unauthorizedHandler();
    }

    return response;
  };
};
